import { span, type Mountable } from '@llui/dom';

/**
 * `<RevealChip>` — per-fact visibility indicator (per 30-design-system.md §7.7).
 *
 * Four states map to four glyphs:
 *   - `public`   — ● solid filled circle (accent)
 *   - `partial`  — ◐ half-circle (revealed to a specific viewer set)
 *   - `hidden`   — ◯ empty circle (GM-only; no Reveal events)
 *   - `redacted` — ⊠ crossed square (explicitly redacted)
 *
 * Clicking the chip should open the viewer-matrix popover (§7.7); the
 * popover itself is wired by the caller via `@llui/components/popover`
 * — this atom only owns the trigger glyph.
 *
 * For `belief.held_as_fact` (`30 §3.3`), pass `dotIndicator: true` so
 * the chip carries the small accessibility dot that distinguishes
 * from canon in non-color contexts.
 *
 * The text label (announced to screen readers) is the caller's
 * responsibility via the i18n catalog — e.g. `t('reveal.public', {
 * count: 4 })` → "Visible to: party (4 PCs)".
 */

export type RevealState = 'public' | 'partial' | 'hidden' | 'redacted';

export interface RevealChipOptions {
  state: RevealState;
  /** Accessible label, e.g. "Visible to: party (4 PCs)". Shown to SR; not painted. */
  label: string;
  /**
   * Optional short painted text beside the glyph (e.g. "Hidden",
   * "Revealed"). When set, the chip widens to a labelled pill — the
   * legible treatment for dense management surfaces (the Facts tab),
   * where a bare 18px glyph is too cryptic and color is never the only
   * signal (§10.8). Omit it for compact glyph-only contexts (infobox
   * reveal marks, table rows). The `label` still carries the full SR text.
   */
  text?: string;
  /** Optional click handler — usually opens the viewer-matrix popover. */
  onClick?: (ev: MouseEvent) => void;
  /** Held-as-fact dot indicator (per §3.3 a11y). */
  dotIndicator?: boolean;
  /** Forwarded `data-testid`. */
  testid?: string;
}

const GLYPHS: Record<RevealState, string> = {
  public: '●',
  partial: '◐',
  hidden: '○',
  redacted: '⊠',
};

export function revealChip(opts: RevealChipOptions): Mountable {
  const glyph = GLYPHS[opts.state];
  const labelled = typeof opts.text === 'string' && opts.text !== '';
  // The glyph is decorative when a painted text label accompanies it (the
  // text + `aria-label` carry the meaning); aria-hidden keeps the SR output
  // from doubling the symbol.
  const children: (string | Mountable)[] = labelled
    ? [span({ class: 'dl-reveal-chip-glyph', 'aria-hidden': 'true' }, [glyph]), opts.text as string]
    : [glyph];
  const klass = labelled ? 'dl-reveal-chip dl-reveal-chip--labelled' : 'dl-reveal-chip';
  if (typeof opts.onClick === 'function') {
    const handler = opts.onClick;
    return span(
      {
        class: klass,
        'data-state': opts.state,
        'aria-label': opts.label,
        title: opts.label,
        role: 'button',
        tabindex: 0,
        'data-dot': opts.dotIndicator ? 'true' : undefined,
        'data-testid': opts.testid,
        onClick: (ev) => handler(ev),
        onKeyDown: (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            handler(ev as unknown as MouseEvent);
          }
        },
      },
      children,
    );
  }
  return span(
    {
      class: klass,
      'data-state': opts.state,
      'aria-label': opts.label,
      title: opts.label,
      'data-dot': opts.dotIndicator ? 'true' : undefined,
      'data-testid': opts.testid,
    },
    children,
  );
}
